import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { getStoreInfo } from '../../api/stores';
import { getNotificationSettings, createNotificationSettings, updateNotificationSettings } from '../../api/notifications';
import { getAccountInfo } from '../../api/auth';


const DEFAULT_SETTINGS = {
    phone_number: '',
    call_alert_enabled: false,
    call_summary_enabled: false,
};

const NotificationsPage = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();

    const [스토어명, set스토어명] = useState('');
    const [로딩중, set로딩중] = useState(true);
    const [저장중, set저장중] = useState(false);
    const [저장완료, set저장완료] = useState(false);
    const [에러, set에러] = useState(null);
    const [설정, set설정] = useState(DEFAULT_SETTINGS);
    const [설정존재, set설정존재] = useState(false);

    useEffect(() => {
        Promise.all([
            getStoreInfo(uuid),
            getNotificationSettings(uuid).catch(() => null),
            getAccountInfo().catch(() => null),
        ])
            .then(([스토어, saved, account]) => {
                set스토어명(스토어?.name ?? '');
                const myPhone = account?.data?.account?.phone_number ?? '';
                const merged = { ...DEFAULT_SETTINGS, ...saved };
                if (!merged.phone_number) merged.phone_number = myPhone;
                set설정존재(!!saved);
                set설정(merged);
            })
            .catch(() => set에러('정보를 불러오지 못했습니다.'))
            .finally(() => set로딩중(false));
    }, [uuid]);

    const update = (field, value) => set설정((prev) => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        set저장중(true);
        set에러(null);
        try {
            const payload = {
                phone_number: 설정.phone_number,
                call_alert_enabled: 설정.call_alert_enabled,
                call_summary_enabled: 설정.call_summary_enabled,
            };
            if (설정존재) {
                await updateNotificationSettings(uuid, payload);
            } else {
                await createNotificationSettings(uuid, payload);
                set설정존재(true);
            }
            set저장완료(true);
            setTimeout(() => set저장완료(false), 2000);
        } catch {
            set에러('저장 중 오류가 발생했습니다.');
        } finally {
            set저장중(false);
        }
    };

    if (로딩중) return (
        <Page><Body><Inner><StatusText>불러오는 중...</StatusText></Inner></Body></Page>
    );

    return (
        <Page>
            <Body>
                <Inner>
                    <BackButton onClick={() => navigate('/stores')}>
                        ← 스토어 목록
                    </BackButton>

                    <TitleArea>
                        <PageBadge>요약 / 알림 설정</PageBadge>
                        <PageTitle>{스토어명 || '스토어'}</PageTitle>
                        <PageDesc>
                            통화가 발생하거나 통화가 끝났을 때 알림을 받을 방법을 설정하세요.<br />
                            수신 전화번호로 실시간 알림과 AI 요약을 받을 수 있습니다.
                        </PageDesc>
                    </TitleArea>

                    {/* 수신 전화번호 */}
                    <Section>
                        <SectionHeader>
                            <SectionTitle>수신 전화번호</SectionTitle>
                            <SectionDesc>AI 에이전트가 응대한 통화의 알림 및 요약을 받을 전화번호를 입력하세요. 통화 알림과 AI 요약이 이 번호로 전송됩니다.</SectionDesc>
                        </SectionHeader>
                        <Field>
                            <FieldLabel>전화번호</FieldLabel>
                            <FieldInput
                                placeholder="예: 010-1234-5678"
                                value={설정.phone_number}
                                onChange={(e) => update('phone_number', e.target.value)}
                            />
                        </Field>
                    </Section>

                    {/* 통화 알림 */}
                    <Section>
                        <SectionHeader>
                            <SectionTitleRow>
                                <SectionTitle>통화 알림</SectionTitle>
                                <Toggle
                                    $on={설정.call_alert_enabled}
                                    onClick={() => update('call_alert_enabled', !설정.call_alert_enabled)}
                                >
                                    <ToggleThumb $on={설정.call_alert_enabled} />
                                </Toggle>
                            </SectionTitleRow>
                            <SectionDesc>통화가 연결되거나 종료될 때 즉시 알림을 받습니다.</SectionDesc>
                        </SectionHeader>

                    </Section>

                    {/* 통화 요약 */}
                    <Section>
                        <SectionHeader>
                            <SectionTitleRow>
                                <SectionTitle>통화 요약 수신</SectionTitle>
                                <Toggle
                                    $on={설정.call_summary_enabled}
                                    onClick={() => update('call_summary_enabled', !설정.call_summary_enabled)}
                                >
                                    <ToggleThumb $on={설정.call_summary_enabled} />
                                </Toggle>
                            </SectionTitleRow>
                            <SectionDesc>통화가 끝난 후 AI가 작성한 통화 요약을 전달받습니다.</SectionDesc>
                        </SectionHeader>

                    </Section>

                    {에러 && <ErrorText>{에러}</ErrorText>}

                    <SaveArea>
                        <SaveButton onClick={handleSave} disabled={저장중}>
                            {저장중 ? '저장 중...' : 저장완료 ? '✓ 저장됐습니다' : '설정 저장'}
                        </SaveButton>
                    </SaveArea>
                </Inner>
            </Body>
        </Page>
    );
};


/* ─────────────────────────────────────────────
   Styled Components
───────────────────────────────────────────── */
const Page = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
    font-family: Pretendard, -apple-system, sans-serif;
`;

const Body = styled.div`
    padding-top: 80px;
    padding-bottom: 60px;
    display: flex;
    justify-content: center;
`;

const Inner = styled.div`
    width: 100%;
    max-width: 560px;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    align-self: flex-start;
    &:hover { color: #333333; }
`;

const TitleArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PageBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.1em;
    text-transform: uppercase;
`;

const PageTitle = styled.h1`
    font-size: 24px;
    font-weight: 800;
    color: #111111;
    letter-spacing: -0.02em;
    margin: 0;
`;

const PageDesc = styled.p`
    font-size: 14px;
    color: #999999;
    line-height: 1.65;
    margin: 0;
`;

const Section = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 16px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SectionTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const SectionTitle = styled.h3`
    font-size: 15px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const SectionDesc = styled.p`
    font-size: 13px;
    color: #aaaaaa;
    margin: 0;
    line-height: 1.5;
`;

const Toggle = styled.button`
    position: relative;
    width: 44px;
    height: 24px;
    border-radius: 100px;
    border: none;
    background: ${({ $on }) => $on ? '#487BFD' : '#e0e0e0'};
    cursor: pointer;
    transition: background 0.2s;
    flex-shrink: 0;
    padding: 0;
`;

const ToggleThumb = styled.div`
    position: absolute;
    top: 3px;
    left: ${({ $on }) => $on ? '23px' : '3px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
    transition: left 0.2s;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FieldLabel = styled.label`
    font-size: 12px;
    font-weight: 600;
    color: #777777;
`;


const FieldInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    padding: 10px 14px;
    font-family: Pretendard;
    font-size: 14px;
    color: #111111;
    outline: none;
    transition: border-color 0.15s;
    &::placeholder { color: #c0c0c0; }
    &:focus { border-color: #487BFD; background: #ffffff; }
    &:disabled { background: #f7f8fa; cursor: not-allowed; }
`;

const ErrorText = styled.span`
    font-size: 13px;
    color: #EF4444;
`;

const SaveArea = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const SaveButton = styled.button`
    background: ${({ disabled, children }) => String(children).includes('✓') ? '#22c55e' : '#487BFD'};
    border: none;
    border-radius: 10px;
    padding: 12px 28px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s, background 0.3s;
    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const StatusText = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    color: #888888;
`;


export default NotificationsPage;
